import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import { DataGrid } from '@mui/x-data-grid'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'

const SEND_OPTIONS = [
  { label: 'Forward', value: 'F' },
  { label: 'Forw. + Catt.', value: 'C' },
  { label: 'None', value: 'N' }
]

const PROTOCOL_OPTIONS = [
  { label: 'SFTP', value: 'S' },
  { label: 'FTP', value: 'F' }
]

const ERROR_MESSAGES = {
  E1: 'Tutti i campi sono obbligatori',
  E2: 'Regola duplicata'
}

const emptyRule = { pseudoCC: '', codCliente: '', sendOption: 'N', protocol: 'S', host: '', user: '', pwd: '', folder: '' }

const getLabelForValue = (options, value) => options.find(opt => opt.value === value)?.label || ''

function ForwardingRules ({ rulesData, makeNewConfig, selectedPath }) {
  const [rules, setRules] = useState(rulesData || [])
  const [newRule, setNewRule] = useState({ ...emptyRule })
  const [editingIndex, setEditingIndex] = useState(null)

  useEffect(() => {
    setRules(rulesData || [])
  }, [rulesData])

  const handleFieldChange = (field) => (e) => {
    setNewRule(prev => ({ ...prev, [field]: e.target.value }))
  }

  const validateRule = (rule) => {
    if ((rule.pseudoCC.trim() === '' && rule.codCliente.trim() === '') ||
      rule.host.trim() === '' || rule.user.trim() === '' || rule.pwd.trim() === '' || rule.folder.trim() === '') {
      return 'E1'
    }

    const alreadyExist = editingIndex == null
      ? rules?.some((r) => r.pseudoCC === rule.pseudoCC && r.codCliente === rule.codCliente)
      : rules?.length > 1 && rules.some((r, index) => index !== editingIndex && r.pseudoCC === rule.pseudoCC && r.codCliente === rule.codCliente)

    if (alreadyExist) return 'E2'
    return ''
  }

  const applyRulesChange = (updatedRules) => {
    makeNewConfig(selectedPath.index, 'forwardingRules', updatedRules)
    setRules(updatedRules)
  }

  const handleSubmit = () => {
    const errorCode = validateRule(newRule)
    if (errorCode) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: ERROR_MESSAGES[errorCode] })
      return
    }

    const updatedRules = editingIndex === null
      ? [...rules, newRule]
      : rules.map((r, i) => (i === editingIndex ? newRule : r))

    applyRulesChange(updatedRules)
    handleClear()
  }

  const handleClear = () => {
    setEditingIndex(null)
    setNewRule({ ...emptyRule })
  }

  const handleEdit = (rule, index) => {
    setEditingIndex(index)
    setNewRule(rule)
  }

  const handleRemove = (index) => {
    applyRulesChange(rules.filter((_, i) => i !== index))
  }

  const columns = [
    { field: 'pseudoCC', headerName: 'Pseudo CC', flex: 1 },
    { field: 'codCliente', headerName: 'Cod Cliente', flex: 1 },
    {
      field: 'sendOption', headerName: 'Invio', flex: 0.8,
      renderCell: (params) => getLabelForValue(SEND_OPTIONS, params.value)
    },
    {
      field: 'protocol', headerName: 'Protocollo', flex: 0.7,
      renderCell: (params) => getLabelForValue(PROTOCOL_OPTIONS, params.value)
    },
    { field: 'host', headerName: 'Host', flex: 1 },
    { field: 'user', headerName: 'User', flex: 0.7 },
    { field: 'pwd', headerName: 'Pwd', flex: 0.7 },
    { field: 'folder', headerName: 'Folder', flex: 1 },
    {
      field: 'actions', headerName: 'Azioni', width: 100, sortable: false, filterable: false,
      renderCell: (params) => {
        const rowIndex = rules.findIndex(r => r.pseudoCC === params.row.pseudoCC && r.codCliente === params.row.codCliente)
        return (
          <Stack direction='row' spacing={0.5}>
            <Tooltip title='Modifica' arrow>
              <IconButton size='small' onClick={() => handleEdit(params.row, rowIndex)} aria-label='Modifica regola'>
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Elimina' arrow>
              <IconButton
                size='small'
                onClick={() => handleRemove(rowIndex)}
                disabled={editingIndex !== null}
                sx={{ color: 'error.main' }}
                aria-label='Elimina regola'
              >
                <DeleteOutlineIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        )
      }
    }
  ]

  const isEditing = editingIndex !== null

  return (
    <Box sx={{ width: '100%' }}>
      <Paper variant='outlined' sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant='subtitle2' sx={{ mb: 1.5, fontWeight: 600 }}>
          {isEditing ? 'Modifica Regola' : 'Nuova Regola'}
        </Typography>
        <Stack spacing={1.5}>
          <Stack direction='row' spacing={1.5}>
            <TextField label='Pseudo CC' value={newRule.pseudoCC} onChange={handleFieldChange('pseudoCC')} size='small' fullWidth />
            <TextField label='Cod Cliente' value={newRule.codCliente} onChange={handleFieldChange('codCliente')} size='small' fullWidth />
            <FormControl size='small' fullWidth>
              <InputLabel>Opzione Invio</InputLabel>
              <Select value={newRule.sendOption} onChange={handleFieldChange('sendOption')} label='Opzione Invio'>
                {SEND_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size='small' fullWidth>
              <InputLabel>Protocollo</InputLabel>
              <Select value={newRule.protocol} onChange={handleFieldChange('protocol')} label='Protocollo'>
                {PROTOCOL_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction='row' spacing={1.5}>
            <TextField label='Host' value={newRule.host} onChange={handleFieldChange('host')} size='small' fullWidth />
            <TextField label='User' value={newRule.user} onChange={handleFieldChange('user')} size='small' fullWidth />
            <TextField label='Password' value={newRule.pwd} onChange={handleFieldChange('pwd')} size='small' fullWidth />
            <TextField label='Folder' value={newRule.folder} onChange={handleFieldChange('folder')} size='small' fullWidth />
          </Stack>

          <Stack direction='row' spacing={1} justifyContent='flex-end'>
            <Button
              variant='outlined'
              size='small'
              startIcon={<ClearIcon />}
              onClick={handleClear}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Annulla
            </Button>
            <Button
              variant='contained'
              size='small'
              startIcon={isEditing ? <SaveIcon /> : <AddIcon />}
              onClick={handleSubmit}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {isEditing ? 'Salva' : 'Aggiungi'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
        Regole Impostate
      </Typography>

      {rules.length === 0
        ? (
          <Typography variant='body2' color='text.secondary' sx={{ py: 3, textAlign: 'center' }}>
            Nessuna regola configurata
          </Typography>
        )
        : (
          <DataGrid
            rows={rules}
            columns={columns}
            getRowId={(row) => `${row.pseudoCC}-${row.codCliente}-${row.host}`}
            initialState={{ pagination: { paginationModel: { pageSize: 8 } } }}
            pageSizeOptions={[8, 25, 50]}
            disableRowSelectionOnClick
            density='compact'
            sx={{ height: Math.max(250, Math.min(rules.length * 42 + 110, 500)), border: 'none' }}
          />
        )}
    </Box>
  )
}

export default ForwardingRules
